import {
  Button,
  Card,
  CardHeader,
  Chip,
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import { IVISAxios } from "../../Modules/Axios/IVISAxios";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ uploadedFiles, vin }) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentKey, setCurrentKey] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfUrls, setPdfUrls] = useState({});
  const [scale, setScale] = useState(1.0);
  const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png"];
  const isPdf = (fileName) => fileName?.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    const loadSinglePdf = async () => {
      if (!currentKey || pdfUrls[currentKey]) return;

      try {
        const res = await IVISAxios.get("/attached", {
          params: { vin, type: currentKey },
          responseType: "blob",
        });
        const blobUrl = URL.createObjectURL(new Blob([res.data]));
        setPdfUrls((prev) => ({ ...prev, [currentKey]: blobUrl }));
      } catch (err) {
        console.error("PDF fetch error:", err);
      }
    };

    if (isViewerOpen) {
      loadSinglePdf();
    }
  }, [currentKey, isViewerOpen]);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  return (
    <Box sx={{ mt: 1.5, position: "sticky", top: 0 }}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Box display="flex" justifyContent="space-between">
              <Box
                display="flex"
                gap={1}
                fontSize={16}
                fontWeight="bold"
                alignItems="center"
                flexShrink={0}
              >
                <Chip
                  label="BETA"
                  color="warning"
                  size="small"
                  sx={{ fontSize: 10, fontWeight: "bold" }}
                />
                PDF 미리보기
                <FormControl size="small" sx={{ maxWidth: 300, width: 300 }}>
                  <Select
                    displayEmpty
                    value={currentKey}
                    onChange={(e) => {
                      const selectedKey = e.target.value;
                      if (selectedKey !== "" && !isViewerOpen) {
                        setIsViewerOpen(true);
                      }
                      setCurrentKey(selectedKey);
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value={""}>
                      ---------파일을 선택하세요--------
                    </MenuItem>
                    {Object.entries(uploadedFiles)
                      .filter(([_, info]) =>
                        allowedExtensions.some((ext) =>
                          info?.fileName?.toLowerCase().endsWith(ext)
                        )
                      )
                      .map(([key, info]) => (
                        <MenuItem key={key} value={key}>
                          {info.fileName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>

              {isViewerOpen &&
                currentKey &&
                isPdf(uploadedFiles[currentKey]?.fileName) && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexShrink={0}
                  >
                    <IconButton
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      sx={{ p: 0 }}
                    >
                      <ArrowLeft />
                    </IconButton>

                    <Typography sx={{ mx: 2 }} fontSize={12}>
                      {currentPage} / {numPages}
                    </Typography>

                    <IconButton
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(numPages, prev + 1))
                      }
                      disabled={currentPage === numPages}
                      sx={{ p: 0 }}
                    >
                      <ArrowRight />
                    </IconButton>
                  </Box>
                )}
            </Box>
          }
          sx={{
            padding: 1,
            borderBottom: "1px solid #e8e8e8",
          }}
        />
        {isViewerOpen && (
          <Box
            maxHeight="calc(100vh - 220px)"
            height="calc(100vh - 220px)"
            overflow="auto"
            backgroundColor="#f5f5f5"
          >
            <Box>
              {currentKey && isPdf(uploadedFiles[currentKey]?.fileName) ? (
                <Document
                  file={pdfUrls[currentKey]}
                  onLoadSuccess={({ numPages }) => {
                    setNumPages(numPages);
                    setCurrentPage(1);
                  }}
                >
                  <Page pageNumber={currentPage} scale={scale} />
                </Document>
              ) : (
                <Box width={600 * scale}>
                  <img
                    src={pdfUrls[currentKey]}
                    alt={uploadedFiles[currentKey]?.fileName}
                    style={{
                      width: `${600 * scale}px`,
                      objectFit: "contain",
                      transition: "all 0.2s ease-in-out",
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box
              position="absolute"
              bottom={30}
              left="50%"
              sx={{
                transform: "translateX(-50%)",
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: 2,
                padding: "4px 12px",
                display: "flex",

                alignItems: "center",
                gap: 1,
                zIndex: 10,
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.6)",
                },
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={handleZoomOut}
                sx={{
                  minWidth: 0,
                  px: 1,
                  backgroundColor: "white",
                  color: "black",
                  "&:hover": { color: "white" },
                }}
              >
                −
              </Button>
              <Typography color="white" fontSize={14}>
                {(scale * 100).toFixed(0)}%
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleZoomIn}
                sx={{
                  minWidth: 0,
                  px: 1,
                  backgroundColor: "white",
                  color: "black",
                  "&:hover": { color: "white" },
                }}
              >
                ＋
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default PDFViewer;
